import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useUsage } from '../hooks/useUsage';
import { GeneratorSettings } from '../types';
import { AVAILABLE_FONTS, canvasToBlob } from '../lib/canvas';
import { saveGeneratedImage } from '../lib/usage';
import ImageCanvas, { ImageCanvasRef } from '../components/ImageCanvas';
import Toast, { useToast } from '../components/Toast';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { AI_QUOTES } from '../lib/quotes';

const DEFAULT_SETTINGS: GeneratorSettings = {
    content: 'The only way to do great work is to love what you do.',
    username: 'Nagaraj_Dev143',
    backgroundType: 'solid',
    backgroundColor: '#0f172a',
    backgroundGradient: ['#a855f7', '#6366f1'],
    textColor: '#ffffff',
    font: "'Plus Jakarta Sans', sans-serif",
    fontSize: 52,
    wordWrapLimit: 60,
    lineHeight: 1.5,
    textAlign: 'center',
    width: 1080,
    height: 1080,
    showDateText: true,
    dateText: new Date().toLocaleDateString(),
};

const SOCIAL_SIZES = [
    { id: 'insta-post', label: 'Instagram', width: 1080, height: 1080, icon: '📱' },
    { id: 'story', label: 'Story', width: 1080, height: 1920, icon: '🎬' },
    { id: 'twitter', label: 'Twitter', width: 1200, height: 675, icon: '🐦' },
    { id: 'desktop', label: 'Desktop', width: 1920, height: 1080, icon: '💻' },
    { id: 'whatsapp', label: 'WhatsApp', width: 1080, height: 1920, icon: '💬' },
];

const TEMPLATES = [
    {
        id: 'liquid-glass-premium', label: '💎 Liquid Glass', settings: {
            layout: 'liquid-glass',
            backgroundGradient: ['#ff0080', '#7928ca'],
            textColor: '#ffffff',
            font: "'Plus Jakarta Sans', sans-serif",
            fontSize: 56,
            backgroundType: 'gradient',
            textAlign: 'center',
            usernamePosition: 'bottom',
            content: 'Design is not just what it looks like and feels like. Design is how it works.'
        }
    },
    {
        id: 'milestone-premium', label: '🎉 Milestone', settings: {
            layout: 'celebration',
            backgroundColor: '#fdfcf0',
            textColor: '#b45309',
            font: "'Outfit', sans-serif",
            fontSize: 72,
            backgroundType: 'solid',
            textAlign: 'center',
            usernamePosition: 'top',
            username: 'Nagaraj_Dev143',
            milestoneCount: '300',
            milestoneColor: '#b45309',
            content: 'FOLLOWERS\nThank you\nfor your engagement and support!'
        }
    },
    {
        id: 'saas-sale-premium', label: '💰 First Sale', settings: {
            layout: 'saas-hero',
            backgroundGradient: ['#0f172a', '#1e293b'],
            textColor: '#10b981',
            font: "'Plus Jakarta Sans', sans-serif",
            fontSize: 60,
            backgroundType: 'gradient',
            content: "Share Visuals just made its first sale",
            saasDay: "FIRST SALE 🎉",
            saasStartupName: "Grateful for the support 🙌",
            saasCurrentValue: '$29',
            showDateText: true,
            showLogo: true
        }
    },
    { id: 'minimal', label: '✨ Minimal', settings: { backgroundColor: '#ffffff', textColor: '#000000', font: "'Plus Jakarta Sans', sans-serif", backgroundType: 'solid', textAlign: 'center', usernamePosition: 'bottom', content: 'Simplicity is the ultimate sophistication.' } },
    { id: 'gradient', label: '✨ Gradient', settings: { backgroundGradient: ['#a855f7', '#6366f1'], textColor: '#ffffff', font: "'Outfit', sans-serif", backgroundType: 'gradient', textAlign: 'center', usernamePosition: 'bottom', content: 'The best way to predict the future is to create it.' } },
    { id: 'business', label: '✨ Business', settings: { backgroundColor: '#0f172a', textColor: '#ffffff', font: "'Plus Jakarta Sans', sans-serif", backgroundType: 'solid', textAlign: 'left', usernamePosition: 'top-left', content: 'Focus on being productive instead of busy.' } },
    { id: 'bold', label: '✨ Bold', settings: { backgroundColor: '#000000', textColor: '#fbbf24', font: "'Outfit', sans-serif", fontSize: 80, backgroundType: 'solid', textAlign: 'center', usernamePosition: 'bottom', content: 'WORK HARD IN SILENCE' } },
    {
        id: 'stats-dark', label: '📊 Stats Card', settings: {
            layout: 'stats',
            backgroundColor: '#000000',
            textColor: '#ffffff',
            font: "'Outfit', sans-serif",
            backgroundType: 'solid',
            content: 'Project Metrics',
            statsTitle: 'Project Metrics',
            stats: [
                { label: 'Weekly Views', value: '12.4K', icon: '👁️' },
                { label: 'Cloud Space', value: '82%', icon: '☁️' },
                { label: 'Total Tasks', value: '1,420', icon: '✅' }
            ],
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true
        }
    },
    {
        id: 'stats-gradient', label: '📈 Stats Glow', settings: {
            layout: 'stats',
            backgroundGradient: ['#1e1b4b', '#312e81'],
            textColor: '#ffffff',
            font: "'Outfit', sans-serif",
            backgroundType: 'gradient',
            content: 'Performance',
            statsTitle: 'Performance',
            stats: [
                { label: 'Reach', value: '45.2K', icon: '🌍' },
                { label: 'Likes', value: '1,280', icon: '❤️' },
                { label: 'Engagement', value: '4.8%', icon: '🔥' }
            ],
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true
        }
    },
    {
        id: 'saas-modern', label: '🚀 SaaS Update', settings: {
            layout: 'saas',
            backgroundColor: '#0f172a',
            textColor: '#ffffff',
            font: "'Plus Jakarta Sans', sans-serif",
            backgroundType: 'solid',
            saasDay: 'Day 6',
            saasStartupName: 'Share Visuals',
            saasTargetGoal: '$1k MRR',
            saasCurrentValue: '$282 MRR',
            saasProgress: 28,
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true
        }
    },
    {
        id: 'saas-pulse', label: '📊 Growth Pulse', settings: {
            layout: 'saas',
            backgroundGradient: ['#020617', '#1e1b4b'],
            textColor: '#ffffff',
            font: "'Montserrat', sans-serif",
            backgroundType: 'gradient',
            saasDay: 'Week 2',
            saasStartupName: 'TurboTask',
            saasTargetGoal: '500 users',
            saasCurrentValue: '120 Users',
            saasProgress: 24,
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true
        }
    },
    {
        id: 'saas-bip', label: '🚀 Build in Public', settings: {
            layout: 'saas',
            backgroundGradient: ['#000000', '#111827'],
            textColor: '#ffffff',
            font: "'Outfit', sans-serif",
            backgroundType: 'gradient',
            content: "🚀 Day 1 of building my SaaS\nStarting with analysis & planning → development begins.\nBuilding in public.\n\nWhat are you building today? Share yours 👇",
            saasCurrentValue: '0 MRR',
            saasProgress: 5,
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true,
            fontSize: 42,
            wordWrapLimit: 55
        }
    },
    { id: 'modern-serif', label: '📜 Serif Classic', settings: { backgroundColor: '#fdfcf0', textColor: '#1a1a1a', font: "'Playfair Display', serif", backgroundType: 'solid', textAlign: 'center', usernamePosition: 'below-content', content: 'Everything you can imagine is real.' } },
    {
        id: 'dev-mode', label: '💻 Dev Stats', settings: {
            layout: 'stats',
            backgroundColor: '#020617',
            textColor: '#22c55e',
            font: "'Fira Code', monospace",
            backgroundType: 'solid',
            content: 'System Status',
            statsTitle: 'System Status',
            stats: [
                { label: 'Server Uptime', value: '99.98%', icon: '🔋' },
                { label: 'API Latency', value: '24ms', icon: '📡' },
                { label: 'Active Nodes', value: '12', icon: '💻' }
            ],
            showLogo: true
        }
    },
    { id: 'sunset-quote', label: '🌅 Sunset', settings: { backgroundGradient: ['#f97316', '#ec4899'], textColor: '#ffffff', font: "'PT Serif', serif", backgroundType: 'gradient', textAlign: 'center', usernamePosition: 'bottom-right', content: 'Chase your dreams until you catch them.' } },
    {
        id: 'ocean-stats', label: '🌊 Ocean Growth', settings: {
            layout: 'stats',
            backgroundGradient: ['#0c4a6e', '#0ea5e9'],
            textColor: '#ffffff',
            font: "'Raleway', sans-serif",
            backgroundType: 'gradient',
            content: 'Platform Growth',
            statsTitle: 'Platform Growth',
            stats: [
                { label: 'Total Sales', value: '$4,520', icon: '💰' },
                { label: 'New Signups', value: '128', icon: '📈' },
                { label: 'Avg Session', value: '12m', icon: '⏱️' }
            ],
            dateText: new Date().toLocaleDateString(),
            showDateText: true,
            showLogo: true
        }
    },
    { id: 'clean-social', label: '📱 Clean Social', settings: { backgroundColor: '#ffffff', textColor: '#1da1f2', font: "'Inter', sans-serif", backgroundType: 'solid', textAlign: 'left', usernamePosition: 'top-left', content: 'Keep it simple, keep it social.' } },
    { id: 'luxury-gold', label: '✨ Luxury Gold', settings: { backgroundGradient: ['#78350f', '#451a03'], textColor: '#fbbf24', font: "'Outfit', sans-serif", backgroundType: 'gradient', textAlign: 'center', usernamePosition: 'bottom', content: 'QUALITY OVER QUANTITY' } },
    { id: 'minimal-pro', label: '💎 Minimal Pro', settings: { backgroundColor: '#fafafa', textColor: '#171717', font: "'Lora', serif", backgroundType: 'solid', textAlign: 'right', usernamePosition: 'bottom-right', content: 'Precision is the soul of elegance.' } },
];

const QUICK_COLORS = [
    '#ffffff', '#000000', '#f3f4f6', '#9ca3af', '#4b5563',
    '#ef4444', '#f97316', '#f59e0b', '#10b981', '#06b6d4',
    '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef', '#ec4899',
    '#0ea5e9', '#14b8a6', '#f43f5e', '#84cc16', '#fbbf24',
    '#1e293b', '#0f172a', '#020617', '#134e4a', '#4c1d95'
];

const GRADIENTS = [
    { name: 'Cosmic', colors: ['#7c3aed', '#06b6d4'] },
    { name: 'Sunset Glow', colors: ['#f59e0b', '#e11d48'] },
    { name: 'Hyper', colors: ['#3b82f6', '#9333ea'] },
    { name: 'Cyberpunk', colors: ['#ff00ff', '#00ffff'] },
    { name: 'Oceanic', colors: ['#06b6d4', '#3b82f6'] },
    { name: 'Lush', colors: ['#10b981', '#34d399'] },
    { name: 'Neon', colors: ['#22c55e', '#06b6d4'] },
    { name: 'Fire', colors: ['#ef4444', '#f59e0b'] },
    { name: 'Royal', colors: ['#4338ca', '#6d28d9'] },
    { name: 'Candy', colors: ['#f472b6', '#60a5fa'] },
    { name: 'Tangerine', colors: ['#f97316', '#fbbf24'] },
    { name: 'Midnight', colors: ['#1e293b', '#0f172a'] },
    { name: 'Emerald Night', colors: ['#065f46', '#10b981'] },
    { name: 'Ametyst', colors: ['#9333ea', '#db2777'] },
    { name: 'Venice', colors: ['#4338ca', '#06b6d4'] },
    { name: 'Forest', colors: ['#14532d', '#84cc16'] },
    { name: 'Deep Sea', colors: ['#020617', '#0891b2'] },
];

const PRESET_BACKGROUND_IMAGES = [
    { name: 'Ventura', url: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?q=80&w=1080' },
    { name: 'Dynamic Peak', url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1080' },
    { name: 'Abstract Flow', url: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?q=80&w=1080' },
    { name: 'Aurora Mesh', url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?q=80&w=1080' },
    { name: 'Soft Silk', url: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=1080' },
    { name: 'Dark Flow', url: 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?q=80&w=1080' },
    { name: 'Glassy Blue', url: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?q=80&w=1080' },
    { name: 'Deep Nebula', url: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?q=80&w=1080' },
    { name: 'Minimal Sand', url: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=1080' },
    { name: 'Vibrant Mesh', url: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?q=80&w=1080' },
];

const Generator: React.FC = () => {
    const { user } = useAuthContext();
    const { profile, isFree, isPro, isLifetime } = useProfile();
    const { usage, incrementUsage, dailyLimit } = useUsage();
    const { toast, showToast, hideToast } = useToast();
    const navigate = useNavigate();

    const [settings, setSettings] = useState<GeneratorSettings>(DEFAULT_SETTINGS);
    const [templateImage, setTemplateImage] = useState<HTMLImageElement | null>(null);
    const [presetBackgroundImage, setPresetBackgroundImage] = useState<HTMLImageElement | null>(null);
    const [saving, setSaving] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // CSV batch state (Pro only)
    const [csvQuotes, setCsvQuotes] = useState<string[]>([]);
    const [batchGenerating, setBatchGenerating] = useState(false);
    const [batchProgress, setBatchProgress] = useState(0);

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const canvasRef = useRef<ImageCanvasRef>(null);

    const isWatermarked = isFree;
    const hasBatchAccess = isPro || isLifetime;

    const updateSetting = useCallback(<K extends keyof GeneratorSettings>(key: K, value: GeneratorSettings[K]) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
    }, []);

    const applyTemplate = (tpl: any) => {
        setSettings(prev => ({ ...prev, ...tpl.settings }));
        setSelectedTemplateId(tpl.id);
        showToast(`${tpl.label} template applied`, 'success');
    };

    const handleTemplateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const img = new Image();
        img.onload = () => setTemplateImage(img);
        img.src = URL.createObjectURL(file);
        updateSetting('backgroundType', 'template');
    };

    const handleSelectPreset = (url: string) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            setPresetBackgroundImage(img);
            updateSetting('presetImageUrl', url);
            updateSetting('backgroundType', 'preset_image');
        };
        img.src = url;
    };

    const handleGenerate = async () => {
        if (!user || !profile) return;
        const allowed = await incrementUsage(profile.plan);
        if (!allowed) {
            setShowUpgradeModal(true);
            return;
        }
        setSaving(true);
        try {
            const canvas = canvasRef.current?.getCanvas();
            if (!canvas) throw new Error('Canvas not ready');
            const blob = await canvasToBlob(canvas);
            const url = await saveGeneratedImage(user.id, blob, settings.content);
            if (url) {
                showToast('Image saved to your dashboard!', 'success');
            } else {
                showToast('Failed to save. Check Supabase storage settings.', 'error');
            }
        } catch {
            showToast('An error occurred while generating.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!user || !profile) return;

        // Check if allowed (but don't increment yet, we increment only on success)
        const currentCount = usage?.count ?? 0;
        if (isFree && currentCount >= dailyLimit) {
            setShowUpgradeModal(true);
            return;
        }

        const canvas = canvasRef.current?.getCanvas();
        if (!canvas) return;

        // For downloads, we also count it as usage if it's a success
        const allowed = await incrementUsage(profile.plan);
        if (!allowed) {
            setShowUpgradeModal(true);
            return;
        }

        const blob = await canvasToBlob(canvas);
        saveAs(blob, `quote-${Date.now()}.png`);
        showToast('Image downloaded!', 'success');
    };

    const generateAIQuote = () => {
        let randomQuote = settings.content;
        while (randomQuote === settings.content) {
            randomQuote = AI_QUOTES[Math.floor(Math.random() * AI_QUOTES.length)];
        }
        updateSetting('content', randomQuote);
        showToast('New AI quote generated!', 'info');
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(settings.content);
        showToast('Quote copied to clipboard!', 'success');
    };

    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result as string;
            const lines = text.split('\n').map((l) => l.replace(/^"|"$/g, '').trim()).filter(Boolean);
            setCsvQuotes(lines);
            showToast(`${lines.length} quotes loaded from CSV`, 'info');
        };
        reader.readAsText(file);
    };

    const handleBatchGenerate = async () => {
        if (!csvQuotes.length || !canvasRef.current?.getCanvas()) return;
        setBatchGenerating(true);
        const zip = new JSZip();
        for (let i = 0; i < csvQuotes.length; i++) {
            setSettings((prev) => ({ ...prev, content: csvQuotes[i] }));
            await new Promise((res) => setTimeout(res, 150));
            const canvas = canvasRef.current?.getCanvas();
            if (canvas) {
                const blob = await canvasToBlob(canvas);
                zip.file(`quote-${i + 1}.png`, blob);
            }
            setBatchProgress(Math.round(((i + 1) / csvQuotes.length) * 100));
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'quotes-batch.zip');
        setBatchGenerating(false);
        setBatchProgress(0);
        showToast('All images downloaded as ZIP!', 'success');
    };

    const [artType, setArtType] = useState<'quotes' | 'stats' | 'saas'>('quotes');

    const filteredTemplates = TEMPLATES.filter(tpl => {
        if (artType === 'stats') return tpl.settings.layout === 'stats' || tpl.settings.layout === 'celebration';
        if (artType === 'saas') return tpl.settings.layout === 'saas' || tpl.settings.layout === 'saas-hero';
        return !tpl.settings.layout || tpl.settings.layout === 'default' || tpl.settings.layout === 'liquid-glass';
    });

    return (
        <div className="min-h-screen bg-dark-950 text-white pb-20">
            <div className="container-page pt-32 pb-8">
                {/* Header */}
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 animate-slide-up">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Visual Generator</h1>
                        <p className="text-dark-400 mt-1 text-sm">Design stunning creator-focused visuals in seconds.</p>
                    </div>
                    {isFree && (
                        <div className="bg-primary-500/10 border border-primary-500/20 px-4 py-2 rounded-xl flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                            <span className="text-sm text-primary-300 font-medium">Ships today: {usage?.count ?? 0}/{dailyLimit}</span>
                        </div>
                    )}
                </div>

                {/* Art Type Toggle & Templates */}
                <div className="mb-10 space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-dark-800 pb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center text-primary-400">
                                🎨
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white tracking-wide">Art Style</h3>
                                <p className="text-[10px] text-dark-500 uppercase tracking-widest font-bold">Switch layouts</p>
                            </div>
                        </div>

                        <div className="flex bg-dark-900 p-1 rounded-xl border border-dark-800 self-start sm:self-center" role="tablist">
                            <button
                                role="tab"
                                aria-selected={artType === 'quotes'}
                                onClick={() => {
                                    setArtType('quotes');
                                    setSettings(prev => ({
                                        ...prev,
                                        layout: 'default',
                                        content: prev.layout === 'stats' || prev.layout === 'saas' ? 'The only way to do great work is to love what you do.' : prev.content,
                                        dateText: prev.dateText || new Date().toLocaleDateString(),
                                        showDateText: prev.showDateText ?? true
                                    }));
                                }}
                                className={`px-6 py-2 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-2 ${artType === 'quotes' ? 'bg-primary-500 text-white shadow-glow' : 'text-dark-400 hover:text-white hover:bg-dark-800'}`}
                            >
                                <span className="text-sm" aria-hidden="true">📝</span>
                                Quote Art
                            </button>
                            <button
                                role="tab"
                                aria-selected={artType === 'stats'}
                                onClick={() => {
                                    setArtType('stats');
                                    setSettings(prev => ({
                                        ...prev,
                                        layout: 'stats',
                                        content: prev.layout !== 'stats' ? 'Month 1' : prev.content,
                                        dateText: prev.dateText || new Date().toLocaleDateString(),
                                        showDateText: true
                                    }));
                                }}
                                className={`px-6 py-2 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-2 ${artType === 'stats' ? 'bg-primary-500 text-white shadow-glow' : 'text-dark-400 hover:text-white hover:bg-dark-800'}`}
                            >
                                <span className="text-sm" aria-hidden="true">📊</span>
                                Stats Card
                            </button>
                            <button
                                role="tab"
                                aria-selected={artType === 'saas'}
                                onClick={() => {
                                    setArtType('saas');
                                    // Load the First Sale (saas-hero) template by default
                                    const saasTpl = TEMPLATES.find(t => t.id === 'saas-sale-premium');
                                    if (saasTpl) {
                                        setSettings(prev => ({
                                            ...prev,
                                            ...(saasTpl.settings as Partial<GeneratorSettings>),
                                            username: prev.username,
                                            dateText: prev.dateText || new Date().toLocaleDateString(),
                                            showDateText: true
                                        }));
                                    }
                                }}
                                className={`px-6 py-2 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-2 ${artType === 'saas' ? 'bg-primary-500 text-white shadow-glow' : 'text-dark-400 hover:text-white hover:bg-dark-800'}`}
                            >
                                <span className="text-sm" aria-hidden="true">🚀</span>
                                SaaS Update
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    {/* ─── Settings Panel (LEFT) ─── */}
                    <div className="space-y-6">

                        {/* Premium Presets */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-[10px] font-bold text-dark-500 uppercase tracking-widest">Presets</h3>
                                <span className="text-[10px] text-dark-600 font-mono">{filteredTemplates.length}</span>
                            </div>
                            <div className="grid grid-cols-5 gap-1.5">
                                {filteredTemplates.map(tpl => (
                                    <button
                                        key={tpl.id}
                                        onClick={() => applyTemplate(tpl)}
                                        aria-label={`Apply ${tpl.label} template`}
                                        className={`py-2 rounded-lg border text-[9px] font-medium transition-all duration-200 flex flex-col items-center gap-1 ${selectedTemplateId === tpl.id ? 'border-primary-500 bg-primary-500/10 text-primary-300' : 'bg-dark-900/40 border-dark-800/60 text-dark-500 hover:border-dark-600 hover:text-dark-300'}`}
                                    >
                                        <span className="text-base leading-none" aria-hidden="true">{tpl.label.split(' ')[0]}</span>
                                        <span className="truncate w-full text-center px-0.5">{tpl.label.split(' ').slice(1).join(' ')}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Social Sizes Selector */}
                        <div className="card-minimal space-y-3">
                            <h2 className="text-[10px] font-bold text-dark-400 uppercase tracking-widest">Post Size</h2>
                            <div className="grid grid-cols-5 gap-2">
                                {SOCIAL_SIZES.map(size => (
                                    <button
                                        key={size.id}
                                        onClick={() => {
                                            updateSetting('width', size.width);
                                            updateSetting('height', size.height);
                                        }}
                                        aria-label={`Switch to ${size.label} size (${size.width}x${size.height})`}
                                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${settings.width === size.width && settings.height === size.height
                                            ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                                            : 'bg-dark-900 border-dark-800 text-dark-400 hover:border-dark-600'
                                            }`}
                                    >
                                        <span className="text-lg" aria-hidden="true">{size.icon}</span>
                                        <span className="text-[9px] font-bold uppercase tracking-tight text-center">{size.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="card-minimal space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-[10px] font-bold text-dark-400 uppercase tracking-widest">
                                    {settings.layout === 'saas' ? 'Main Text' : settings.layout === 'celebration' ? 'Milestone Label' : settings.layout === 'stats' ? 'Stats Title' : 'Content'}
                                </h2>
                                {settings.layout !== 'stats' && settings.layout !== 'saas' && settings.layout !== 'celebration' && (
                                    <button
                                        onClick={generateAIQuote}
                                        className="text-[9px] font-bold text-primary-400 uppercase tracking-widest flex items-center gap-1 hover:text-primary-300 transition-colors"
                                    >
                                        ✨ AI Magic
                                    </button>
                                )}
                            </div>

                            <div className="relative group">
                                <label htmlFor="quote-content" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">
                                    {settings.layout === 'saas' ? 'Display Text' : settings.layout === 'celebration' ? 'Milestone Label' : settings.layout === 'stats' ? 'Title Text' : 'Quote Text'}
                                </label>
                                <textarea
                                    id="quote-content"
                                    rows={3}
                                    value={settings.content}
                                    onChange={(e) => updateSetting('content', e.target.value)}
                                    placeholder={settings.layout === 'saas' ? 'Day 6 of growing my startup Share Visuals to $1k MRR' : (settings.layout === 'stats' || settings.layout === 'celebration') ? 'Stats title...' : 'Enter your quote here...'}
                                    className="input-minimal resize-none"
                                />
                                <button
                                    onClick={copyToClipboard}
                                    className="absolute right-2 bottom-2 p-1.5 bg-dark-800 rounded-md text-dark-400 hover:text-white opacity-0 group-hover:opacity-100 transition-all shadow-xl"
                                    aria-label="Copy Text"
                                    title="Copy Text"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>

                            {/* Date / Badge for Default (Quote) layout */}
                            {(settings.layout === 'default' || !settings.layout || settings.layout === 'liquid-glass') && (
                                <div className="flex items-center gap-4 pt-1 border-t border-dark-800/60 mt-2">
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Badge / Date</label>
                                            <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => updateSetting('showDateText', !settings.showDateText)}>
                                                <input type="checkbox" id="showDateDefault"
                                                    checked={settings.showDateText}
                                                    onChange={(e) => updateSetting('showDateText', e.target.checked)}
                                                    className="w-3 h-3 rounded border-dark-700 bg-dark-900 text-primary-500"
                                                />
                                                <label htmlFor="showDateDefault" className="text-[9px] uppercase font-bold text-dark-600 tracking-tight cursor-pointer">Show</label>
                                            </div>
                                        </div>
                                        <input
                                            id="date-text-default"
                                            type="text"
                                            value={settings.dateText || ''}
                                            onChange={(e) => updateSetting('dateText', e.target.value)}
                                            disabled={!settings.showDateText}
                                            className={`input-minimal ${!settings.showDateText ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
                                            placeholder={new Date().toLocaleDateString()}
                                            aria-label="Badge or Date text"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* SaaS Hero / Storytelling Fields */}
                            {settings.layout === 'saas-hero' && (
                                <div className="space-y-3 pt-1 border-t border-dark-800/60">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Hero Headline</label>
                                            <input
                                                type="text"
                                                value={settings.saasDay || ''}
                                                onChange={(e) => updateSetting('saasDay', e.target.value)}
                                                className="input-minimal"
                                                placeholder="FIRST SALE 🎉"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="saas-hero-amount" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Hero Amount</label>
                                            <input
                                                id="saas-hero-amount"
                                                type="text"
                                                value={settings.saasCurrentValue || ''}
                                                onChange={(e) => updateSetting('saasCurrentValue', e.target.value)}
                                                className="input-minimal"
                                                placeholder="$29"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider block">Emotional Quote / Footer</label>
                                        <input
                                            type="text"
                                            value={settings.saasStartupName || ''}
                                            onChange={(e) => updateSetting('saasStartupName', e.target.value)}
                                            className="input-minimal"
                                            placeholder="The journey begins 🚀"
                                        />
                                    </div>
                                    <div className="flex items-center gap-4 pt-1 border-t border-dark-800/60 ">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Badge / Date</label>
                                                <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => updateSetting('showDateText', !settings.showDateText)}>
                                                    <input type="checkbox" id="showDateSaaS"
                                                        checked={settings.showDateText}
                                                        onChange={(e) => updateSetting('showDateText', e.target.checked)}
                                                        className="w-3 h-3 rounded border-dark-700 bg-dark-900 text-primary-500"
                                                    />
                                                    <label htmlFor="showDateSaaS" className="text-[9px] uppercase font-bold text-dark-600 tracking-tight cursor-pointer">Show</label>
                                                </div>
                                            </div>
                                            <input
                                                id="saas-date-text"
                                                type="text"
                                                value={settings.dateText || ''}
                                                onChange={(e) => updateSetting('dateText', e.target.value)}
                                                disabled={!settings.showDateText}
                                                className={`input-minimal ${!settings.showDateText ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
                                                placeholder="02/26/2026"
                                                aria-label="SaaS Update Date"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SaaS Progress Bar Fields */}
                            {settings.layout === 'saas' && (
                                <div className="space-y-3 pt-1 border-t border-dark-800/60">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Current Value</label>
                                            <input
                                                type="text"
                                                value={settings.saasCurrentValue || ''}
                                                onChange={(e) => updateSetting('saasCurrentValue', e.target.value)}
                                                className="input-minimal"
                                                placeholder="$282"
                                            />
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Progress</label>
                                                <span className="text-[10px] font-bold text-green-400">{settings.saasProgress ?? 0}%</span>
                                            </div>
                                            <input
                                                type="range" min={0} max={100}
                                                value={settings.saasProgress ?? 0}
                                                onChange={(e) => updateSetting('saasProgress', Number(e.target.value))}
                                                className="w-full h-1 bg-dark-800 rounded-lg appearance-none cursor-pointer accent-green-500 mt-2"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 pt-1 border-t border-dark-800/60">
                                        <div className="flex-1">
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Date / Label</label>
                                            <input
                                                id="saas-progress-date"
                                                type="text"
                                                value={settings.dateText || ''}
                                                onChange={(e) => updateSetting('dateText', e.target.value)}
                                                className="input-minimal"
                                                placeholder="3/6/2026"
                                                aria-label="Progress Date Label"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Celebration-specific fields (Milestone) */}
                            {settings.layout === 'celebration' && (
                                <div className="space-y-4 pt-4 border-t border-dark-800/60">
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Date / Subtitle</label>
                                        <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => updateSetting('showDateText', !settings.showDateText)}>
                                            <input type="checkbox" id="showDateCel"
                                                checked={settings.showDateText}
                                                onChange={(e) => updateSetting('showDateText', e.target.checked)}
                                                className="w-3 h-3 rounded border-dark-700 bg-dark-900 text-primary-500"
                                            />
                                            <label htmlFor="showDateCel" className="text-[9px] uppercase font-bold text-dark-600 tracking-tight cursor-pointer">Show</label>
                                        </div>
                                    </div>
                                    <input
                                        id="celebration-subtitle"
                                        type="text"
                                        value={settings.dateText || ''}
                                        onChange={(e) => updateSetting('dateText', e.target.value)}
                                        disabled={!settings.showDateText}
                                        className={`input-minimal ${!settings.showDateText ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
                                        placeholder="02/12 - 2026"
                                        aria-label="Celebration Subtitle"
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Milestone Count</label>
                                            <input
                                                type="text"
                                                value={settings.milestoneCount ?? ''}
                                                onChange={(e) => updateSetting('milestoneCount', e.target.value)}
                                                className="input-minimal"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Number Color</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={settings.milestoneColor || settings.textColor}
                                                    onChange={(e) => updateSetting('milestoneColor', e.target.value)}
                                                    className="w-8 h-8 rounded border-dark-700 bg-none cursor-pointer"
                                                />
                                                <input
                                                    type="text"
                                                    value={settings.milestoneColor || settings.textColor}
                                                    onChange={(e) => updateSetting('milestoneColor', e.target.value)}
                                                    className="input-minimal font-mono text-[10px]"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Stats inline fields */}
                            {settings.layout === 'stats' && (
                                <div className="space-y-3 pt-1 border-t border-dark-800/60">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Date / Subtitle</label>
                                                <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => updateSetting('showDateText', !settings.showDateText)}>
                                                    <input type="checkbox" id="showDateStats"
                                                        checked={settings.showDateText}
                                                        onChange={(e) => updateSetting('showDateText', e.target.checked)}
                                                        className="w-3 h-3 rounded border-dark-700 bg-dark-900 text-primary-500"
                                                    />
                                                    <label htmlFor="showDateStats" className="text-[9px] uppercase font-bold text-dark-600 tracking-tight cursor-pointer">Show</label>
                                                </div>
                                            </div>
                                            <input
                                                id="stats-date-text"
                                                type="text"
                                                value={settings.dateText || ''}
                                                onChange={(e) => updateSetting('dateText', e.target.value)}
                                                disabled={!settings.showDateText}
                                                className={`input-minimal ${!settings.showDateText ? 'opacity-40 grayscale cursor-not-allowed' : ''}`}
                                                placeholder="02/12 - 2026"
                                                aria-label="Stats Date Label"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider block">Stat Items</label>
                                        {(settings.stats || []).map((stat, idx) => (
                                            <div key={idx} className="flex gap-1.5" role="group" aria-label={`Stat item ${idx + 1}`}>
                                                <input type="text" value={stat.icon}
                                                    aria-label="Stat Icon"
                                                    onChange={(e) => { const s = [...(settings.stats || [])]; s[idx].icon = e.target.value; updateSetting('stats', s); }}
                                                    className="w-9 input-minimal text-center" placeholder="✨" />
                                                <input type="text" value={stat.value}
                                                    aria-label="Stat Value"
                                                    onChange={(e) => { const s = [...(settings.stats || [])]; s[idx].value = e.target.value; updateSetting('stats', s); }}
                                                    className="flex-1 input-minimal" placeholder="Value" />
                                                <input type="text" value={stat.label}
                                                    aria-label="Stat Label"
                                                    onChange={(e) => { const s = [...(settings.stats || [])]; s[idx].label = e.target.value; updateSetting('stats', s); }}
                                                    className="flex-1 input-minimal" placeholder="Label" />
                                                <button 
                                                    onClick={() => { const s = (settings.stats || []).filter((_, i) => i !== idx); updateSetting('stats', s); }}
                                                    className="px-1.5 text-dark-600 hover:text-red-400 transition-colors text-xs"
                                                    aria-label={`Remove stat ${idx + 1}`}
                                                >✕</button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => { const s = [...(settings.stats || []), { label: 'Metric', value: '100', icon: '✨' }]; updateSetting('stats', s); }}
                                            className="w-full py-1.5 border border-dashed border-dark-700 rounded-lg text-[10px] text-dark-500 hover:border-dark-500 hover:text-dark-300 transition-all font-bold uppercase tracking-widest"
                                        >
                                            + Add Stat
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" id="showLogo2"
                                            checked={settings.showLogo}
                                            onChange={(e) => updateSetting('showLogo', e.target.checked)}
                                            className="w-3.5 h-3.5 rounded border-dark-700 bg-dark-900 text-primary-500"
                                        />
                                        <label htmlFor="showLogo2" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Show Logo</label>
                                    </div>
                                </div>
                            )}

                            {/* Alignment Controls — only for quotes */}
                            {settings.layout !== 'stats' && settings.layout !== 'saas' && settings.layout !== 'celebration' && (
                                <div className="space-y-2">
                                    <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider">Text Alignment</label>
                                    <div className="flex gap-1 p-0.5 bg-dark-950 rounded-lg border border-dark-800">
                                        {(['left', 'center', 'right'] as const).map(align => (
                                            <button
                                                key={align}
                                                role="radio"
                                                aria-checked={settings.textAlign === align}
                                                aria-label={`Align text ${align}`}
                                                onClick={() => updateSetting('textAlign', align)}
                                                className={`flex-1 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide transition-all ${settings.textAlign === align
                                                    ? 'bg-dark-800 text-white shadow-lg border border-dark-700'
                                                    : 'text-dark-500 hover:text-dark-300'
                                                    }`}
                                            >
                                                {align === 'left' && 'Left'}
                                                {align === 'center' && 'Center'}
                                                {align === 'right' && 'Right'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-1 space-y-3">
                                <label htmlFor="quote-username" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Username & Position</label>
                                <div className="relative">
                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-dark-400 text-xs select-none">@</span>
                                    <input
                                        id="quote-username"
                                        type="text"
                                        value={settings.username}
                                        onChange={(e) => updateSetting('username', e.target.value)}
                                        placeholder="username"
                                        className="input-minimal pl-7"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    {(['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right', 'top', 'bottom', 'below-content'] as const).map(pos => (
                                        <button
                                            key={pos}
                                            aria-label={`Position username at ${pos.replace('-', ' ')}`}
                                            aria-pressed={settings.usernamePosition === pos}
                                            onClick={() => updateSetting('usernamePosition', pos)}
                                            className={`py-1 px-1 rounded border text-[9px] font-bold uppercase transition-all ${settings.usernamePosition === pos
                                                ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                                                : 'bg-dark-900 border-dark-800 text-dark-500 hover:text-dark-300'
                                                }`}
                                        >
                                            {pos.replace('-', ' ')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Background */}
                        <div className="card-minimal space-y-4">
                            <h2 className="text-[10px] font-bold text-dark-400 uppercase tracking-widest">Background</h2>

                            <div className="flex gap-1 p-0.5 bg-dark-950 rounded-lg border border-dark-800">
                                {(['solid', 'gradient', 'preset_image', 'template'] as const).map(type => (
                                    <button
                                        key={type}
                                        role="radio"
                                        aria-checked={settings.backgroundType === type}
                                        onClick={() => updateSetting('backgroundType', type)}
                                        className={`flex-1 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${settings.backgroundType === type
                                            ? 'bg-dark-800 text-white shadow-sm border border-dark-700'
                                            : 'text-dark-500 hover:text-dark-300'
                                            }`}
                                    >
                                        {type === 'solid' && 'Solid'}
                                        {type === 'gradient' && 'Grad'}
                                        {type === 'preset_image' && 'Preset'}
                                        {type === 'template' && 'Custom'}
                                    </button>
                                ))}
                            </div>

                            {settings.backgroundType === 'solid' && (
                                <div className="space-y-4">
                                    {/* 5x5 Color Grid - Fixed 50x50px */}
                                    <div className="flex flex-wrap gap-2">
                                        {QUICK_COLORS.map(color => (
                                            <button
                                                key={color}
                                                onClick={() => updateSetting('backgroundColor', color)}
                                                aria-label={`Select background color ${color}`}
                                                className={`w-[42px] h-[42px] rounded-lg transition-all transform hover:scale-105 relative ${settings.backgroundColor === color ? 'ring-1 ring-white ring-offset-1 ring-offset-dark-900 border border-white' : 'border border-dark-700'}`}
                                                style={{ backgroundColor: color }}
                                            >
                                                {settings.backgroundColor === color && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <svg className="w-2.5 h-2.5 text-white drop-shadow-md" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="flex items-center gap-3">
                                            <label htmlFor="bg-color-picker" className="sr-only">Choose background color</label>
                                            <input
                                                id="bg-color-picker"
                                                type="color"
                                                value={settings.backgroundColor}
                                                onChange={(e) => updateSetting('backgroundColor', e.target.value)}
                                                className="w-10 h-10 rounded-lg cursor-pointer border-2 border-dark-700 bg-transparent p-0.5"
                                            />
                                            <span className="text-dark-400 text-sm font-mono uppercase">{settings.backgroundColor}</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-dark-500 uppercase">Custom Pick</span>
                                    </div>
                                </div>
                            )}

                            {settings.backgroundType === 'gradient' && (
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        {GRADIENTS.map(grad => (
                                            <button
                                                key={grad.name}
                                                onClick={() => updateSetting('backgroundGradient', grad.colors)}
                                                aria-label={`Select ${grad.name} gradient background`}
                                                className={`w-[42px] h-[42px] rounded-lg transition-all transform hover:scale-105 relative ${JSON.stringify(settings.backgroundGradient) === JSON.stringify(grad.colors) ? 'ring-1 ring-white ring-offset-1 ring-offset-dark-900 border border-white' : 'border border-dark-700'}`}
                                                style={{ background: `linear-gradient(135deg, ${grad.colors[0]}, ${grad.colors[1]})` }}
                                                title={grad.name}
                                            >
                                                {JSON.stringify(settings.backgroundGradient) === JSON.stringify(grad.colors) && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <svg className="w-2.5 h-2.5 text-white drop-shadow-md" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {settings.backgroundType === 'preset_image' && (
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        {PRESET_BACKGROUND_IMAGES.map(bg => (
                                            <button
                                                key={bg.name}
                                                onClick={() => handleSelectPreset(bg.url)}
                                                aria-label={`Select ${bg.name} preset background image`}
                                                className={`w-[42px] h-[42px] rounded-lg transition-all transform hover:scale-105 relative overflow-hidden ${settings.presetImageUrl === bg.url ? 'ring-1 ring-white ring-offset-1 ring-offset-dark-900 border border-white' : 'border border-dark-700'}`}
                                                title={bg.name}
                                            >
                                                <img src={bg.url} alt={bg.name} className="w-full h-full object-cover" />
                                                {settings.presetImageUrl === bg.url && (
                                                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                        <svg className="w-3 h-3 text-white drop-shadow-md" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {settings.backgroundType === 'template' && (
                                <div>
                                    <label className="label">Upload Template Image</label>
                                    <div className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-dark-700 rounded-2xl bg-dark-900/50 hover:bg-dark-800 transition-colors cursor-pointer relative">
                                        <input
                                            id="template-upload"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleTemplateUpload}
                                            className="absolute inset-0 opacity-0 cursor-pointer"
                                        />
                                        <svg className="w-8 h-8 text-dark-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-sm text-dark-400">Click to upload or drag & drop</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Typography */}
                        <div className="card-minimal space-y-4">
                            <h2 className="text-[10px] font-bold text-dark-400 uppercase tracking-widest">Typography</h2>

                            <div className="grid grid-cols-2 gap-3">
                                {/* Font */}
                                <div className="col-span-2">
                                    <label htmlFor="font-select" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Font Family</label>
                                    <select
                                        id="font-select"
                                        value={settings.font}
                                        onChange={(e) => updateSetting('font', e.target.value)}
                                        className="input-minimal"
                                    >
                                        {AVAILABLE_FONTS.map((f) => (
                                            <option key={f.value} value={f.value}>{f.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Text color */}
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-1 block">Color</label>
                                    <div className="flex items-center gap-2">
                                        <label htmlFor="text-color-picker" className="sr-only">Choose text color</label>
                                        <input
                                            id="text-color-picker"
                                            type="color"
                                            value={settings.textColor}
                                            onChange={(e) => updateSetting('textColor', e.target.value)}
                                            className="w-8 h-8 rounded-md cursor-pointer border-2 border-dark-700 bg-transparent p-0.5"
                                        />
                                        <span className="text-dark-400 text-[11px] font-mono uppercase">{settings.textColor}</span>
                                    </div>
                                </div>

                                {/* Font size */}
                                <div>
                                    <label htmlFor="font-size" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-0.5 block">
                                        Size <span className="text-primary-400">{settings.fontSize}</span>
                                    </label>
                                    <input
                                        id="font-size"
                                        type="range" min={20} max={160}
                                        value={settings.fontSize}
                                        onChange={(e) => updateSetting('fontSize', Number(e.target.value))}
                                        className="w-full h-1.5 bg-dark-800 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                    />
                                </div>

                                {/* Word wrap */}
                                <div>
                                    <label htmlFor="word-wrap" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-0.5 block">
                                        Wrap <span className="text-primary-400">{settings.wordWrapLimit}</span>
                                    </label>
                                    <input
                                        id="word-wrap"
                                        type="range" min={10} max={120}
                                        value={settings.wordWrapLimit}
                                        onChange={(e) => updateSetting('wordWrapLimit', Number(e.target.value))}
                                        className="w-full h-1.5 bg-dark-800 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                    />
                                </div>

                                {/* Line height */}
                                <div>
                                    <label htmlFor="line-height" className="text-[10px] uppercase font-bold text-dark-500 tracking-wider mb-0.5 block">
                                        Height <span className="text-primary-400">{settings.lineHeight.toFixed(1)}</span>
                                    </label>
                                    <input
                                        id="line-height"
                                        type="range" min={1.0} max={2.5} step={0.1}
                                        value={settings.lineHeight}
                                        onChange={(e) => updateSetting('lineHeight', Number(e.target.value))}
                                        className="w-full h-1.5 bg-dark-800 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Batch Generation — Pro only */}
                        {hasBatchAccess && (
                            <div className="card bg-gradient-card border-primary-500/30 shadow-glow space-y-4">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-xs font-bold text-primary-300 uppercase tracking-widest">Batch Generation</h2>
                                    <span className="badge-pro">PRO</span>
                                </div>
                                <p className="text-sm text-dark-400">Upload a CSV with one quote per line to generate all at once.</p>
                                <label htmlFor="csv-upload" className="sr-only">Upload CSV or TXT file</label>
                                <input
                                    id="csv-upload"
                                    type="file" accept=".csv,.txt"
                                    onChange={handleCsvUpload}
                                    className="block w-full text-sm text-dark-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-500 file:text-white hover:file:bg-primary-400 cursor-pointer"
                                />
                                {csvQuotes.length > 0 && (
                                    <div>
                                        <p className="text-sm text-dark-200 mb-3">{csvQuotes.length} quotes loaded</p>
                                        {batchGenerating ? (
                                            <div>
                                                <div className="h-2 w-full bg-dark-700 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-primary transition-all"
                                                        style={{ width: `${batchProgress}%` }}
                                                    />
                                                </div>
                                                <p className="text-xs text-dark-400 mt-2">{batchProgress}% complete...</p>
                                            </div>
                                        ) : (
                                            <button id="batch-generate-btn" onClick={handleBatchGenerate} className="btn-primary w-full">
                                                Generate All & Download ZIP
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ─── Preview + Actions (RIGHT, sticky) ─── */}
                    <div className="flex flex-col gap-6 sticky top-24">
                        {/* Unique Browser-style Window container */}
                        <div className="relative group">
                            {/* Accent Glow behind preview */}
                            <div className="absolute -inset-2 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-[32px] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                            <div className="relative bg-dark-900 border border-white/5 rounded-[28px] overflow-hidden shadow-2xl transition-all duration-500 hover:border-white/10">
                                {/* Window Header Bar */}
                                <div className="bg-white/[0.03] backdrop-blur-md px-5 py-3.5 border-b border-white/5 flex items-center justify-between">
                                    <div className="flex items-center gap-6">
                                        {/* Browser Dots */}
                                        <div className="flex gap-1.5  group-hover:grayscale-0 group-hover:opacity-100 transition-all">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                                        </div>
                                        <h3 className="text-[10px] font-black text-dark-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                            <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse" />
                                            Live Preview
                                        </h3>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {/* Dynamic Header Actions */}
                                        <div className="flex items-center gap-2 pr-3 border-r border-white/10">
                                            <button
                                                onClick={handleGenerate}
                                                disabled={saving}
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 transition-all active:scale-95 disabled:opacity-50 group/save"
                                                title="Save to Dashboard"
                                                aria-label="Save visual to dashboard"
                                            >
                                                {saving ? (
                                                    <div className="w-3.5 h-3.5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <svg className="w-3.5 h-3.5 text-primary-400 group-hover/save:scale-110 transition-transform" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                                    </svg>
                                                )}
                                                <span className="text-[10px] font-black uppercase tracking-wider text-dark-100 italic">Save</span>
                                            </button>

                                            <button
                                                onClick={handleDownload}
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-500 border border-primary-400/20 transition-all active:scale-95 shadow-glow-sm group/ship"
                                                title="Download Visual"
                                                aria-label="Download visual to your computer"
                                            >
                                                <svg className="w-3.5 h-3.5 text-white group-hover/ship:translate-y-0.5 transition-transform" fill="none" stroke="currentColor" aria-hidden="true" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                <span className="text-[10px] font-black uppercase tracking-wider text-white italic">Ship</span>
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white/[0.05] border border-white/5">
                                            <svg className="w-3 h-3 text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                                            <span className="text-[10px] font-mono text-dark-300">
                                                {settings.width} × {settings.height} px
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Preview Canvas Area */}
                                <div className="p-5 md:p-8 bg-[#0B101E]/40">
                                    <ImageCanvas
                                        ref={canvasRef}
                                        settings={settings}
                                        isWatermarked={isWatermarked || false}
                                        templateImage={templateImage}
                                        presetImage={presetBackgroundImage}
                                        width={settings.width}
                                        height={settings.height}
                                        isLimited={isFree && (usage?.count ?? 0) >= dailyLimit}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Watermark notice */}
                        {isWatermarked && (
                            <div className="flex items-center gap-3 bg-primary-500/10 border border-primary-500/30 rounded-2xl px-5 py-4 group animate-pulse-slow">
                                <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
                                    <span className="text-lg">✨</span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-primary-200 text-sm font-medium">
                                        ✨ Ship like a Founder: Remove watermark & unlock premium templates
                                    </p>
                                    <button onClick={() => navigate('/upgrade')} className="text-xs text-primary-400 font-bold uppercase tracking-wider mt-1 hover:text-white transition-colors underline decoration-2 underline-offset-4">
                                        Upgrade to Share Visuals Pro
                                    </button>
                                </div>
                            </div>
                        )}


                    </div>
                </div>
            </div >

            {/* ─── Upgrade Modal ─── */}
            {
                showUpgradeModal && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center px-4 bg-dark-950/80 backdrop-blur-sm">
                        <div className="card max-w-md w-full shadow-2xl animate-fade-in">
                            <div className="w-14 h-14 rounded-2xl bg-warning-500/15 border border-warning-500/30 flex items-center justify-center mx-auto mb-5">
                                <svg className="w-7 h-7 text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-white text-center">Shipping Limit Reached</h2>
                            <p className="text-dark-300 text-sm text-center mt-2">
                                You've reached your daily shipping limit. Upgrade to Pro to ship unlimited visuals.
                            </p>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowUpgradeModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Maybe Later
                                </button>
                                <button
                                    onClick={() => navigate('/upgrade')}
                                    className="btn-accent flex-1"
                                >
                                    View Plans
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        </div>
    );
};

export default Generator;
